import dynamic from 'next/dynamic';
import React, { FC, HTMLAttributes, ComponentType } from 'react';
import { PopperProps } from 'react-popper';
import styled from 'styled-components';

const Popper = dynamic<PopperProps>(() => import('react-popper').then(mod => mod.Popper), { ssr: false });

const Tooltip: FC<
    React.PropsWithChildren<
        Omit<PopperProps, 'children'> & {
            containerProps?: HTMLAttributes<HTMLDivElement>;
            ArrowComponent?: ComponentType<React.PropsWithChildren<unknown>>;
        }
    >
> = ({ containerProps = {}, children, ArrowComponent, ...props }) => (
    <Popper {...props}>
        {({ ref, style, placement, arrowProps }) => (
            <Container {...containerProps} style={{ ...containerProps.style, ...style }} ref={ref}>
                {ArrowComponent ? (
                    <ArrowComponent {...arrowProps} data-placement={placement} />
                ) : (
                    <Arrow {...arrowProps} data-placement={placement} />
                )}
                {children}
            </Container>
        )}
    </Popper>
);

export default Tooltip;

export const Arrow = styled.div`
    position: absolute;
    width: 3em;
    height: 3em;
    display: flex;
    --backgroundColor: ${({ theme }) => theme.colors.neutral['100']};
    &[data-placement*='bottom'] {
        top: 0;
        left: 0;
        margin-top: -2.8rem;
        &::before {
            border-width: 0 0.8rem 1em 0.8rem;
            border-color: transparent transparent var(--backgroundColor) transparent;
        }
    }
    &[data-placement*='top'] {
        bottom: 0;
        left: 0;
        margin-bottom: -2.9em;
        &::before {
            border-width: 1em 0.8rem 0 0.8rem;
            border-color: var(--backgroundColor) transparent transparent transparent;
        }
    }
    &[data-placement*='right'] {
        left: 0;
        margin-left: -3.2rem;
        &::before {
            border-width: 0.8rem 1em 0.8rem 0;
            border-color: transparent var(--backgroundColor) transparent transparent;
        }
    }
    &[data-placement*='left'] {
        right: 0;
        margin-right: -2.8rem;
        &::before {
            border-width: 0.8rem 0 0.8rem 1em;
            border-color: transparent transparent transparent var(--backgroundColor);
        }
    }
    &::before {
        content: '';
        margin: auto;
        display: block;
        width: 0;
        height: 0;
        border-style: solid;
    }
`;

export const Container = styled.div`
    opacity: 0.8;
    color: white;
    width: 30rem;
    padding: 1.6rem;
    background: ${({ theme }) => theme.colors.neutral['100']};
    border-radius: 0.8rem;
    z-index: 9;
`;
